import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowRightLeft, Download, Upload, ShieldCheck, AlertTriangle, Loader2, Database, Copy, FolderInput,
} from "lucide-react";

const ACTIONS = [["copy", "Copy (source stays untouched)"], ["move", "Move (relocate this dashboard)"]];
const CONFLICTS = [["copy", "Keep both — save as a copy"], ["overwrite", "Overwrite the existing dashboard"], ["cancel", "Cancel if a name clash exists"]];

// Item 25: secure dashboard portability — copy/move across sites & tenants, package download/upload.
export function PortabilityDialog({ open, onOpenChange, mode = "replicate", dashboard, onDone }) {
  const isImport = mode === "import";
  const [targets, setTargets] = useState(null);
  const [tenantId, setTenantId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [action, setAction] = useState("copy");
  const [name, setName] = useState("");
  const [conflict, setConflict] = useState("copy");
  const [plan, setPlan] = useState(null);
  const [approve, setApprove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [pkgName, setPkgName] = useState("");
  const [includeData, setIncludeData] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPlan(null); setApprove(false); setBusy(false); setConflict("copy"); setAction("copy");
    setPkg(null); setPkgName(""); setIncludeData(false);
    setName(dashboard?.name || "");
    api.get("/dashboard-portability/targets").then(({ data }) => {
      setTargets(data);
      const firstTenant = data.tenants?.[0]?.id || "";
      setTenantId(firstTenant);
      const s = (data.sites || []).find((x) => x.tenant_id === firstTenant);
      setSiteId(s?.id || "");
    }).catch(() => setTargets({ can_port: false, tenants: [], sites: [] }));
  }, [open, dashboard?.name]);

  const sites = (targets?.sites || []).filter((s) => s.tenant_id === tenantId);
  useEffect(() => {
    if (sites.length && !sites.some((s) => s.id === siteId)) setSiteId(sites[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, targets]);

  const runPlan = useCallback(async () => {
    if (!siteId) return;
    setBusy(true);
    try {
      const body = { target_site_id: siteId, target_tenant_id: tenantId };
      if (isImport) {
        if (!pkg) { setBusy(false); return; }
        body.package = pkg;
      } else {
        body.dashboard_id = dashboard.id;
      }
      const { data } = await api.post("/dashboard-portability/plan", body);
      setPlan(data);
      if (data.conflict) setConflict("copy");
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not check the target"); setPlan(null); }
    setBusy(false);
  }, [siteId, tenantId, isImport, pkg, dashboard]);

  useEffect(() => { if (open && siteId && (isImport ? !!pkg : !!dashboard?.id)) runPlan(); }, [open, siteId, pkg, runPlan, isImport, dashboard?.id]);

  const pickFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed.format !== "eip.dashboard.v1") { toast.error("Not a dashboard package file"); return; }
      setPkg(parsed);
      setPkgName(file.name);
      setName(parsed.dashboard?.name || "Imported dashboard");
    } catch { toast.error("That file could not be read as a dashboard package"); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/dashboard-portability/${dashboard.id}/package`,
        { params: { include_data: includeData }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(dashboard.name || "dashboard").replace(/\s+/g, "_")}.eipdash.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Package downloaded — no credentials or share links are included");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const common = {
        target_site_id: siteId, target_tenant_id: tenantId, on_conflict: conflict,
        approve_dataset_copy: approve, name: name || undefined,
      };
      const { data } = isImport
        ? (await api.post("/dashboard-portability/import", { ...common, package: pkg }))
        : (await api.post(`/dashboard-portability/${dashboard.id}/replicate`, { ...common, action }));
      toast.success(isImport ? "Dashboard imported" : action === "move" ? "Dashboard moved" : "Dashboard copied — the source was not changed");
      onOpenChange(false);
      onDone && onDone(data.dashboard);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "dataset_approval_required") {
        toast.error("Approve copying the missing dataset(s) to continue");
        setPlan((p) => ({ ...(p || {}), missing_datasets: d.missing_datasets || [], needs_dataset_approval: true }));
      } else if (d && typeof d === "object" && d.code === "name_conflict") {
        toast.error(d.message || "Name conflict in the target site");
      } else {
        toast.error(typeof d === "string" ? d : "Operation failed");
      }
    }
    setBusy(false);
  };

  const missing = plan?.missing_datasets || [];
  const blocked = missing.length > 0 && !approve;
  const canSubmit = !!siteId && !busy && !blocked && (!isImport || !!pkg);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="portability-dialog" className="max-w-lg max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {isImport ? <><Upload className="w-4 h-4" />Import dashboard package</> : <><ArrowRightLeft className="w-4 h-4" />Copy or move dashboard</>}
          </DialogTitle>
        </DialogHeader>

        {targets && targets.can_port === false ? (
          <div data-testid="portability-forbidden" className="text-sm text-[color:var(--ink-2)] rounded-xl border border-[color:var(--glass-border)] p-3 flex gap-2">
            <ShieldCheck className="w-4 h-4 text-[color:var(--brand-ocean)] shrink-0 mt-0.5" />
            Only a Super Admin, or an admin of both the source and target tenant/site, can copy or move dashboards.
          </div>
        ) : (
          <div className="space-y-3">
            {isImport && (
              <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2">
                <Label className="text-xs text-[color:var(--ink-3)]">Package file (.eipdash.json)</Label>
                <input ref={fileRef} data-testid="portability-file-input" type="file" accept=".json,application/json"
                  onChange={(e) => pickFile(e.target.files?.[0])} className="text-xs" />
                {pkg && (
                  <div data-testid="portability-pkg-summary" className="text-[11px] text-[color:var(--ink-2)]">
                    {pkgName} · {(pkg.dashboard?.widgets || []).length} widgets · {(pkg.datasets || []).length} dataset(s)
                    {pkg.origin?.includes_data ? " · includes data" : " · definitions only"}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-[color:var(--ink-3)]">Target tenant</Label>
                <select data-testid="portability-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                  className="mt-1 w-full h-8 text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2">
                  {(targets?.tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-[color:var(--ink-3)]">Target site / client</Label>
                <select data-testid="portability-site" value={siteId} onChange={(e) => setSiteId(e.target.value)}
                  className="mt-1 w-full h-8 text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2">
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>

            {!isImport && (
              <div>
                <Label className="text-xs text-[color:var(--ink-3)]">Action</Label>
                <select data-testid="portability-action" value={action} onChange={(e) => setAction(e.target.value)}
                  className="mt-1 w-full h-8 text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2">
                  {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}

            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Name in target</Label>
              <Input data-testid="portability-name" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 h-8 text-xs bg-[color:var(--surface)]" placeholder="Dashboard name" />
            </div>

            {plan?.conflict && (
              <div data-testid="portability-conflict" className="rounded-xl border border-[color:var(--warning,#F6A821)]/50 bg-[color:var(--surface-2)] p-3 space-y-2">
                <div className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--brand-sun,#F6A821)]" />
                  “{plan.conflict.name}” already exists in this site
                </div>
                <select data-testid="portability-conflict-mode" value={conflict} onChange={(e) => setConflict(e.target.value)}
                  className="w-full h-8 text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2">
                  {CONFLICTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}

            <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--ink-2)] flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5" />Data dependencies
              </div>
              {busy && !plan ? <div className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" />Checking the target…</div> : null}
              {plan && Object.keys(plan.matched_datasets || {}).length > 0 && (
                <div data-testid="portability-matched" className="text-[11px] text-[color:var(--ink-3)]">
                  Reusing in target: {Object.values(plan.matched_datasets).map((m) => m.name).join(", ")}
                </div>
              )}
              {missing.length > 0 ? (
                <>
                  <div data-testid="portability-missing" className="text-[11px] text-[color:var(--ink-2)]">
                    No matching dataset in the target for: <b>{missing.map((m) => m.name).join(", ")}</b>
                  </div>
                  <label className="flex items-start gap-2 text-[11px] text-[color:var(--ink-2)] cursor-pointer">
                    <Switch data-testid="portability-approve-datasets" checked={approve} onCheckedChange={setApprove} />
                    <span>I approve copying the data source &amp; dataset into the target. Connection credentials are never copied — live datasets must be re-connected there.</span>
                  </label>
                </>
              ) : plan ? (
                <div className="text-[11px] text-emerald-600">All datasets resolved in the target.</div>
              ) : null}
            </div>

            {!isImport && (
              <div className="rounded-xl border border-[color:var(--glass-border)] p-3 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--ink-2)] flex items-center gap-1.5">
                  <Download className="w-3.5 h-3.5" />Download a portable package
                </div>
                <label className="flex items-center gap-2 text-[11px] text-[color:var(--ink-2)]">
                  <Switch data-testid="portability-include-data" checked={includeData} onCheckedChange={setIncludeData} />
                  Include dataset rows (uploaded data only, max 50k rows)
                </label>
                <Button data-testid="portability-download-btn" size="sm" variant="outline" onClick={download} disabled={busy}
                  className="h-8 text-xs bg-[color:var(--surface-2)] w-full">
                  <Download className="w-3.5 h-3.5 mr-1.5" />Download .eipdash.json
                </Button>
              </div>
            )}
          </div>
        )}

        {targets?.can_port !== false && (
          <DialogFooter>
            <Button data-testid="portability-submit" onClick={submit} disabled={!canSubmit}
              className="bg-[color:var(--brand-ocean)] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : isImport ? <FolderInput className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {isImport ? "Import into site" : action === "move" ? "Move dashboard" : "Copy dashboard"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
